import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  ModalBody,
  ModalContent,
  ModalFooter,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import {
  type ChainFullId,
  ChainFullIds,
  type RawPkh,
  type UnifiedTokenBalance,
  getChainAddress,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import {
  type Provider,
  type QuoteRequest,
  type QuoteResponse,
  RampOperation,
  RampProviderError,
  RampProviderId,
  generateWidgetUrl,
  getQuote,
} from "@umami/ramp";
import { useCurrentAccount, useGetNetworkByFullId, useGetNetworkChain } from "@umami/state";
import { useCustomToast } from "@umami/utils";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { FormPageHeader } from "../SendFlow/FormPageHeader";
import { type FormPageProps, FormSubmitButton, formDefaultValues } from "../SendFlow/utils";

import {
  ArrowForwardIcon,
  CoinbaseIcon,
  MoonpayIcon,
  MtPelerinIcon,
  TezSolidIcon,
  TransakIcon,
  WertIcon,
} from "@/assets/icons";
import { useColor } from "@/styles/useColor";

export const supportedProviders = [
  RampProviderId.Transak,
  RampProviderId.MtPelerin,
  RampProviderId.Coinbase,
  RampProviderId.Wert,
];
const REFETCH_INTERVAL = 30000; // 30 seconds

export type FormValues = {
  sender: RawPkh;
  recipient: RawPkh;
  prettyAmount: string;
  chainId: ChainFullId;
};

export type QuoteParams = {
  cryptoCurrency: string;
  fiatCurrency: string;
  walletAddress: string;
  network: string;
};

export const FormPage = (
  props: FormPageProps<FormValues> & {
    name: string;
    token?: UnifiedTokenBalance;
    onProviderSelect: (amount: string, providers: Provider[], quoteParams: QuoteParams) => void;
    onBuyClick: (apiUrl: string, openInNewTab: boolean) => void;
    tokenSymbol: string;
    network: ChainFullId;
    fiatCurrency?: string;
    iconUrl?: string;
  }
) => {
  const color = useColor();
  const currentAccount = useCurrentAccount()!;
  const { formValues } = useDynamicModalContext();
  const tokenSymbol = props.tokenSymbol;
  const fiatCurrency = props.fiatCurrency ?? "$";
  const toast = useCustomToast();
  const networkConfig = useGetNetworkByFullId()(props.network);
  const getNetworkChain = useGetNetworkChain();

  const form = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: {
      ...formDefaultValues(props),
      chainId: props.chainId ?? ChainFullIds.TEZOS,
    },
  });

  const {
    formState: { errors },
    register,
    handleSubmit,
    watch,
    setValue,
  } = form;

  const prettyAmount = watch("prettyAmount");
  const [debouncedAmount, setDebouncedAmount] = useState(formValues.amount ?? "0");
  const [countdown, setCountdown] = useState(REFETCH_INTERVAL / 1000);

  useEffect(() => {
    // Skip the initial debounce if we're using formValues.amount
    if (prettyAmount === formValues.amount) {
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedAmount(prettyAmount);
    }, 1000);

    return () => clearTimeout(timer);
  }, [prettyAmount, formValues.amount]);

  const fetchQuotes = useCallback(async (): Promise<Provider[]> => {
    const amountValue = parseFloat(debouncedAmount);

    if (!debouncedAmount || isNaN(amountValue) || amountValue <= 0) {
      return [];
    }

    const quoteRequests: QuoteRequest[] = supportedProviders.map(provider => ({
      provider,
      cryptoCurrency: tokenSymbol,
      fiatCurrency,
      fiatAmount: amountValue,
      walletAddress: getChainAddress(currentAccount, props.network).pkh,
      network: networkConfig.chain,
    }));

    const quotes = await Promise.all(
      quoteRequests.map(async req => {
        try {
          return await getQuote(req);
        } catch (error) {
          if (error instanceof RampProviderError) {
            console.warn(`Quote failed for provider ${req.provider}:`, {
              message: error.message,
              statusCode: error.statusCode,
              response: error.response,
            });
          } else {
            console.error(`Unexpected error for provider ${req.provider}:`, error);
          }

          return null;
        }
      })
    );

    const filteredQuotes = quotes.filter((q): q is QuoteResponse => Boolean(q));

    if (filteredQuotes.length === 0 && quoteRequests.length > 0) {
      toast({
        description: `There are no providers available for ${tokenSymbol} on ${networkConfig.name}. Please try a different amount or token.`,
        status: "error",
        isClosable: true,
        id: "no-providers-available",
      });
    }

    const providers: Provider[] = filteredQuotes.map(quote => ({
      id: quote.provider,
      name: quote.provider,
      icon: quote.provider,
      fee: quote.estimatedNetworkFee.toString(),
      estimatedTime: quote.estimatedNetworkFee.toString(),
      price: {
        cryptoAmount: quote.cryptoAmount,
        usd: quote.fiatAmount,
      },
    }));

    providers.sort((a, b) => b.price.cryptoAmount - a.price.cryptoAmount);

    return providers;
  }, [
    debouncedAmount,
    tokenSymbol,
    fiatCurrency,
    props.network,
    toast,
    currentAccount,
    networkConfig,
  ]);

  const {
    data: providers = [],
    isError: providerError,
    isFetching,
  } = useQuery({
    queryKey: [
      "getQuotes",
      debouncedAmount,
      tokenSymbol,
      fiatCurrency,
      formValues.sender,
      props.network,
    ],
    queryFn: fetchQuotes,
    enabled: !!debouncedAmount && parseFloat(debouncedAmount) > 0,
    refetchInterval: 30000,
    retry: 3,
  });

  // Reset countdown every time a fetch happens
  useEffect(() => {
    if (!isFetching) {
      setCountdown(REFETCH_INTERVAL / 1000);
    }
  }, [isFetching]);

  // Decrement countdown every second
  useEffect(() => {
    if (!countdown || countdown <= 0) {
      return;
    }

    const interval = setInterval(() => {
      setCountdown(prev => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown]);

  const selectedProvider = formValues.provider as Provider | undefined;

  const providerToDisplay =
    providers.length > 0
      ? (providers.find(p => p.id === selectedProvider?.id) ?? providers[0])
      : null;

  const getDisplayAmount = () => {
    if (!prettyAmount || !providerToDisplay?.price.cryptoAmount) {
      return `0.00 ${tokenSymbol}`;
    }

    return `${tokenSymbol} ${providerToDisplay.price.cryptoAmount.toFixed(6)}`;
  };

  const handleQuickAmountClick = (amount: string) => {
    setValue("prettyAmount", amount, { shouldValidate: true });
    setDebouncedAmount(amount);
  };

  return (
    <FormProvider {...form}>
      <ModalContent>
        <form onSubmit={handleSubmit(() => {})}>
          <FormPageHeader title={`Buy ${props.name}`} />
          <Flex
            alignItems="center"
            justifyContent="center"
            flex="0 0 auto"
            width="full"
            marginRight="12px"
            marginBottom="32px"
          >
            {props.name === "XTZ" ? (
              <TezSolidIcon width="48px" height="48px" />
            ) : props.iconUrl ? (
              <Image
                width="48px"
                height="48px"
                borderRadius="full"
                fallbackSrc="./static/coin-front.svg"
                src={props.iconUrl}
              />
            ) : (
              <Image width="48px" height="48px" src="./static/coin-front.svg" />
            )}
          </Flex>
          <ModalBody gap="24px">
            <FormControl isInvalid={!!errors.prettyAmount}>
              <InputGroup>
                <Input
                  isDisabled={false}
                  step="0.01"
                  type="number"
                  {...register("prettyAmount", {
                    required: "Amount is required",
                  })}
                  defaultValue={formValues.amount}
                  placeholder="0.00"
                />
                <InputRightElement
                  marginRight="16px"
                  color={color("600")}
                  fontSize="18px"
                  fontWeight="700"
                >
                  $
                </InputRightElement>
              </InputGroup>
              {errors.prettyAmount && (
                <FormErrorMessage data-testid="amount-error">
                  {errors.prettyAmount.message}
                </FormErrorMessage>
              )}
              <Flex
                alignItems="center"
                justifyContent="space-between"
                marginTop="3"
                marginBottom="2"
              >
                <Flex alignItems="center" gap="2">
                  {isFetching ? (
                    <Skeleton
                      width="60px"
                      height="24px"
                      endColor="gray.300"
                      startColor="gray.200"
                    />
                  ) : (
                    <Text color={color("600")} fontSize="lg">
                      {getDisplayAmount()}
                    </Text>
                  )}
                </Flex>
                <Flex gap="2">
                  <Button
                    fontWeight="400"
                    onClick={() => handleQuickAmountClick("100")}
                    paddingX="12px"
                    size="sm"
                    variant="iconButtonSolid"
                  >
                    $100
                  </Button>
                  <Button
                    fontWeight="400"
                    onClick={() => handleQuickAmountClick("500")}
                    paddingX="12px"
                    size="sm"
                    variant="iconButtonSolid"
                  >
                    $500
                  </Button>
                  <Button
                    fontWeight="400"
                    onClick={() => handleQuickAmountClick("1000")}
                    paddingX="12px"
                    size="sm"
                    variant="iconButtonSolid"
                  >
                    $1000
                  </Button>
                </Flex>
              </Flex>
            </FormControl>

            {prettyAmount && (isFetching || providers.length > 0) && (
              <Box>
                {isFetching ? (
                  <Text color={color("800")} fontSize="14px" fontWeight={600}>
                    Fetching providers
                  </Text>
                ) : (
                  <Flex justifyContent="space-between">
                    <Text color={color("800")} fontSize="14px" fontWeight={600}>
                      Pay with
                    </Text>
                    <Text color={color("800")} fontSize="14px" fontWeight={600}>
                      New quote : {countdown < 10 ? `0.0${countdown}` : `0.${countdown}`}
                    </Text>
                  </Flex>
                )}
                <Box
                  marginTop="4"
                  padding={4}
                  background="gray.50"
                  border={isFetching ? "none" : `1px solid ${color("blueDark")}`}
                  borderRadius="12px"
                  _hover={{ background: providerToDisplay ? "gray.200" : "gray.50" }}
                  pointerEvents={providerToDisplay ? "auto" : "none"}
                  cursor={providerToDisplay ? "pointer" : "default"}
                  onClick={() => {
                    if (providers.length > 0) {
                      props.onProviderSelect(prettyAmount, providers, {
                        cryptoCurrency: tokenSymbol,
                        fiatCurrency,
                        walletAddress: currentAccount.address.pkh,
                        network: networkConfig.chain,
                      });
                    }
                  }}
                >
                  {isFetching ? (
                    <Flex flexDirection="column" gap="3">
                      <Flex>
                        <Skeleton
                          width="66%"
                          height="20px"
                          marginRight="4"
                          endColor="gray.300"
                          startColor="gray.200"
                        />
                        <Skeleton
                          width="30%"
                          height="20px"
                          endColor="gray.300"
                          startColor="gray.200"
                        />
                      </Flex>
                      <Flex>
                        <Skeleton
                          width="66%"
                          height="20px"
                          marginRight="4"
                          endColor="gray.300"
                          startColor="gray.200"
                        />
                        <Skeleton
                          width="30%"
                          height="20px"
                          endColor="gray.300"
                          startColor="gray.200"
                        />
                      </Flex>
                    </Flex>
                  ) : providerToDisplay ? (
                    <Flex alignItems="center" gap="10px">
                      <Icon as={getProviderIcon(providerToDisplay.id)} boxSize="24px" />
                      <Flex flexDirection="column" flex="1" gap="4px">
                        <Flex alignItems="center" gap="2">
                          <Text color={color("600")} fontWeight="400">
                            {providerToDisplay.name.charAt(0).toUpperCase() +
                              providerToDisplay.name.slice(1)}
                          </Text>
                          {(!selectedProvider || providerToDisplay.id === providers[0].id) && (
                            <Text
                              color={color("green")}
                              fontSize="14px"
                              fontWeight="400"
                              lineHeight="18px"
                              background="gray.100"
                              borderRadius="100px"
                              paddingX="12px"
                              paddingY="6px"
                            >
                              Best quote
                            </Text>
                          )}
                        </Flex>
                        <Text color={color("500")} fontWeight="400">
                          {providerToDisplay.price.cryptoAmount} {tokenSymbol} ≈$
                          {providerToDisplay.price.usd.toFixed(2)}
                        </Text>
                      </Flex>
                      <Icon as={ArrowForwardIcon} boxSize="24px" />
                    </Flex>
                  ) : null}
                </Box>
              </Box>
            )}
            {providerError && (
              <Text marginBottom="2" color="red.500" fontSize="sm">
                Failed to fetch quotes from any provider. Please try again later.
              </Text>
            )}
            <FormControl isInvalid={!!errors.recipient}>
              {errors.recipient && (
                <FormErrorMessage data-testid="recipient-error">
                  {errors.recipient.message}
                </FormErrorMessage>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <FormSubmitButton
              isDisabled={isFetching || providers.length === 0}
              isLoading={isFetching}
              onClick={() => {
                if (providerToDisplay) {
                  props.onBuyClick(
                    generateWidgetUrl({
                      operation: RampOperation.BUY,
                      provider: providerToDisplay.id,
                      walletId: getChainAddress(currentAccount, props.network).pkh,
                      currency: tokenSymbol,
                      fiatAmount: providerToDisplay.price.usd,
                      network: getNetworkChain(props.network),
                      email: currentAccount.label,
                    }),
                    providerToDisplay.id === RampProviderId.Coinbase
                  );
                }
              }}
              title="Buy"
            />
          </ModalFooter>
        </form>
      </ModalContent>
    </FormProvider>
  );
};

export const getProviderIcon = (providerId: string) => {
  switch (providerId.toLowerCase()) {
    case "moonpay":
      return MoonpayIcon;
    case "transak":
      return TransakIcon;
    case "mtpelerin":
      return MtPelerinIcon;
    case "coinbase":
      return CoinbaseIcon;
    case "wert":
      return WertIcon;
    default:
      return undefined;
  }
};
